import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, ArchiveBoxIcon, EyeIcon } from "@heroicons/react/24/outline";

const ProductViewers: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [product, setProduct] = useState<any>(null);
    const [viewers, setViewers] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const initialized = useRef(false);

    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const [name, setName] = useState<string>("");

    const startIndex = currentPage * itemsPerPage;

    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/product/admin/${id}`);
                if (response?.data?.success) {
                    setProduct(response.data.data);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        })();
    }, [id]);

    async function getViewers() {
        try {
            setDataLoad(true);
            const params: any = { page: currentPage + 1, limit: itemsPerPage };
            if (name) params.search = name;
            const response = await apiFunctions.get(`/product/admin/${id}/viewers`, { params });
            if (response?.data?.success) {
                setViewers(response.data.data);
                settotalRecords(response.data.totalRecords);
                settotalPages(response.data.totalPages);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
        }
        getViewers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, name]);

    const handlePageChange = (newPage: number) => setCurrentPage(newPage);
    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
        }
    };

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">
            <div className="col-span-12 sm:col-span-6 flex items-center gap-3 flex-wrap">
                {product?.imageUrls?.length > 0 ? (
                    <img src={product.imageUrls[0]} alt={product.name} style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "6px" }} />
                ) : (
                    <ArchiveBoxIcon style={{ width: "36px", height: "36px", color: "#d1d5db" }} />
                )}
                <div>
                    <h2 className="card-title m-0 text-lg sm:text-xl">{product?.name || "Product"} — Viewers</h2>
                    <p className="m-0 text-xs" style={{ color: "#9ca3af" }}>
                        {totalRecords} total view{totalRecords === 1 ? "" : "s"}
                    </p>
                </div>
            </div>
            <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by name or mobile..."
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>
            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];
            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate(`/admin/products/view/${id}`)}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            {isMobile ? (
                <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                    <div className="p-3 pb-2">{header}</div>
                    <div className="flex flex-col gap-2 px-3 pb-3">
                        {viewers.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <EyeIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No one has viewed this product yet</p>
                            </div>
                        ) : (
                            viewers.map((rowData: any, idx: number) => (
                                <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                    <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.viewerName || "-"}</p>
                                    <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                        <div><span className="font-medium">Mobile:</span> {rowData.mobileno || "-"}</div>
                                        <div>{formatDateTime(rowData.createddate)}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <DataTable
                    scrollable
                    scrollHeight="calc(100vh - 285px)"
                    style={{ minHeight: "calc(100vh - 210px)", background: "#fff" }}
                    header={header}
                    value={viewers}
                    loading={dataLoad}
                    rows={itemsPerPage}
                    emptyMessage={
                        <div style={{ textAlign: "center", padding: "64px 0" }}>
                            <EyeIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No one has viewed this product yet</p>
                        </div>
                    }
                >
                    <Column field="viewerName" header="Name" style={{ minWidth: "200px" }} body={(rowData) => rowData.viewerName || "-"} />
                    <Column field="mobileno" header="Mobile" style={{ minWidth: "150px" }} body={(rowData) => rowData.mobileno || "-"} />
                    <Column
                        field="createddate"
                        header="Viewed On"
                        style={{ minWidth: "220px" }}
                        body={(rowData) => formatDateTime(rowData.createddate)}
                    />
                </DataTable>
            )}
            {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "0px 0px 8px 8px" }}>
                <Paginator
                    template={paginatoreTemplate}
                    first={startIndex}
                    rows={itemsPerPage}
                    totalRecords={totalRecords}
                />
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                        disabled={currentPage === 0}
                    >
                        <span className="sm:hidden">Prev</span>
                        <span className="hidden sm:inline">Previous</span>
                    </Button>

                    <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                        <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                            Page {currentPage + 1} of {totalPages || 1}
                        </span>
                    </Button>

                    <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                        disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                    >
                        Next
                    </Button>
                </div>
            </div>}
        </div>
    );
};

export default ProductViewers;
